
import { useEffect, useRef, useState, useCallback } from 'react';

interface ErrorLog {
  timestamp: number;
  message: string;
  labels: Record<string, string>;
  severity: string;
  level?: string;
}

interface MonitorConfig {
  errorLevels?: string[];
  pollInterval?: number;
  customer?: string;
  location?: string;
  namespace?: string | string[];
  application?: string;
  environment?: string;
  debug?: boolean;
  protocols?: string[];
  enabled?: boolean;
  // how far back to fetch logs on initial connect
  lookbackMinutes?: number; // default: 60 (1 hour)
}

const STORAGE_KEY = 'loki_error_monitor_last_timestamp';

interface ClearCommand {
  logs: ErrorLog[];
  timestamp: string;
}

export const useLokiErrorMonitor = (wsUrl: string, config?: MonitorConfig) => {
  const [errorLogs, setErrorLogs] = useState<ErrorLog[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);
  const [clearCommands, setClearCommands] = useState<ClearCommand[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const backoffRef = useRef<number>(1000);
  const destroyedRef = useRef<boolean>(false);
  const lastTimestampRef = useRef<number | null>(null);

  const debug = !!config?.debug;
  const safeLog = (...args: any[]) => {
    if (debug) console.log('[useLokiErrorMonitor]', ...args);
  };


  const getStartTime = useCallback((): number => {

    const lookbackMinutes = config?.lookbackMinutes ?? 2880;
    const lookbackMs = lookbackMinutes * 60 * 1000;
    const startTime = Date.now() - lookbackMs;

    safeLog(`Looking back ${lookbackMinutes} minutes (${lookbackMinutes / 1440} days)`);
    return startTime * 1000000; // Convert to nanoseconds
  }, [config?.lookbackMinutes]);

  useEffect(() => {
    if (!config?.enabled) {
      safeLog('enabled=false → force disconnect');

      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (wsRef.current) {
        try {
          wsRef.current.close(1000, 'disabled');
        } catch { }
        wsRef.current = null;
      }

      setIsConnected(false);
      setIsMonitoring(false);
      setIsLoadingHistorical(false);
      setErrorLogs([]);
    }
  }, [config?.enabled]);


  const connect = useCallback(() => {
    if (!config?.enabled) {
      safeLog('connect() skipped: disabled');
      return;
    }
    if (destroyedRef.current) {
      safeLog('connect() ignored: hook is destroyed');
      return;
    }

    if (wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)) {
      safeLog('connect() ignored: socket already open/connecting');
      return;
    }

    try {
      safeLog('Attempting WebSocket connection to', wsUrl);
      const ws = config?.protocols ? new WebSocket(wsUrl, config.protocols) : new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (destroyedRef.current) {
          safeLog('onopen: destroyed during connect; closing socket');
          try { ws.close(1000, 'client destroyed during connect'); } catch (e) { /* ignore */ }
          return;
        }

        setIsConnected(true);
        setError(null);
        backoffRef.current = 1000;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          switch (message.type) {
            case 'connected':

              break;

            case 'error_logs':
           

              if (message.logs && message.logs.length > 0) {


                setErrorLogs(prev => {
                  // Merge logs, sort by timestamp descending, remove duplicates
                  const combined = [...message.logs, ...prev];
                  const unique = Array.from(
                    new Map(combined.map(log => [
                      `${log.timestamp}-${log.message}`,
                      log
                    ])).values()
                  ).sort((a, b) => b.timestamp - a.timestamp);


                  return unique.slice(0, 100); // Keep last 100
                });

                if (message.isHistorical) {
                  setIsLoadingHistorical(false);
                } else {

                }
              } else if (message.isHistorical) {
                setIsLoadingHistorical(false);
              }
              break;

            case 'toast_logs':
          

              if (message.logs && message.logs.length > 0) {
                setErrorLogs(prev => {
                  // Merge all logs - frontend will process Set/Clear
                  const combined = [...message.logs, ...prev];
                  const unique = Array.from(
                    new Map(combined.map(log => [
                      `${log.timestamp}-${log.message}`,
                      log
                    ])).values()
                  ).sort((a, b) => b.timestamp - a.timestamp);

                  return unique.slice(0, 100);
                });
              }

              // Use isHistorical ONLY for loading indicator
              if (message.isHistorical) {
                setIsLoadingHistorical(false);
              }
              break;

            case 'monitoring_started':
              setIsMonitoring(true);
              setIsLoadingHistorical(true);

              break;

            case 'monitoring_stopped':
              setIsMonitoring(false);
              setIsLoadingHistorical(false);

              break;

            case 'monitoring_error':

              setError(message.message);
              setIsLoadingHistorical(false);
              break;

            case 'pong':
              safeLog('pong received');
              break;

            case 'clear_command':
          
              // Pass CLEAR command to the toast component for processing
              if (message.logs && message.logs.length > 0) {
                setClearCommands(prev => [...prev, {
                  logs: message.logs,
                  timestamp: message.timestamp || new Date().toISOString()
                }]);
              }
              break;

            default:
              safeLog('unhandled message type', message.type);
              break;
          }
        } catch (err) {
          console.error('Failed to parse message:', err);
        }
      };

      ws.onerror = (ev) => {
        console.error(' WebSocket error:', ev);
        setError('WebSocket connection error');
      };

      ws.onclose = () => {
        setIsConnected(false);
        setIsMonitoring(false);
        setIsLoadingHistorical(false);
        wsRef.current = null;

        if (destroyedRef.current || !config?.enabled) {
          safeLog('onclose: not reconnecting (destroyed or disabled)');
          return;
        }

        const delay = backoffRef.current;
        safeLog(`Scheduling reconnect in ${delay}ms`);
        reconnectTimeoutRef.current = window.setTimeout(() => {
          backoffRef.current = Math.min(backoffRef.current * 2, 30000);
          connect();
        }, delay);
      };

    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      setError('Failed to create WebSocket connection');
    }
  }, [wsUrl, config?.protocols]); // Removed saveTimestamp - localStorage disabled

  const startMonitoring = useCallback((customConfig?: MonitorConfig) => {
    if (!config?.enabled) {
      safeLog('startMonitoring skipped: disabled');
      return;
    }
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected (startMonitoring)');
      return;
    }

    const finalConfig = { ...config, ...customConfig };
    const startTime = getStartTime();

    wsRef.current.send(JSON.stringify({
      action: 'start_error_monitoring',
      pollInterval: finalConfig.pollInterval || 5000,
      customer: finalConfig.customer,
      location: finalConfig.location,
      namespace: finalConfig.namespace,
      application: finalConfig.application,
      environment: finalConfig.environment,
      startTime // Send calculated start time to backend
    }));
  }, [config, getStartTime]);

  const stopMonitoring = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      action: 'stop_error_monitoring'
    }));
  }, []);

  const clearErrors = useCallback(() => {
    setErrorLogs([]);
    // Optionally clear stored timestamp
    try {
      localStorage.removeItem(STORAGE_KEY);
      lastTimestampRef.current = null;
    } catch (e) {
      safeLog('Failed to clear stored timestamp:', e);
    }
  }, []);

  const disconnect = useCallback(() => {
    safeLog('disconnect called');
    destroyedRef.current = true;

    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      try {
        wsRef.current.close(1000, 'client disconnect');
      } catch (e) {
        // ignore
      }
      wsRef.current = null;
    }

    setIsConnected(false);
    setIsMonitoring(false);
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    destroyedRef.current = false;

    if (config?.enabled) {
      connect();
    } else {
      safeLog('Auto-connect skipped: disabled');
    }

    return () => {
      disconnect();
    };
  }, [config?.enabled, connect, disconnect]);

  // Heartbeat
  useEffect(() => {
    if (!isConnected) return;

    const interval = window.setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ action: 'ping' }));
        } catch (e) {
          safeLog('ping failed', e);
        }
      }
    }, 30000);

    return () => window.clearInterval(interval);
  }, [isConnected]);

  return {
    errorLogs,
    clearCommands,
    isConnected,
    isMonitoring,
    isLoadingHistorical,
    error,
    startMonitoring,
    stopMonitoring,
    clearErrors,
    reconnect: connect,
    disconnect
  };
};

export default useLokiErrorMonitor;